/**
 * 지금 이 화면에 무엇이 떠 있나 — 크게, 넘겨 가며.
 *
 * 모니터링에서 화면을 눌렀을 때 **실제 그림**이 바로 나와야 한다. 항목 목록과
 * 판단 근거는 그 다음이다. 장애 대응은 "무엇이 나가고 있나"를 눈으로 확인하는
 * 데서 시작한다.
 *
 * 편성의 다른 항목도 넘겨 볼 수 있다 — "지금은 괜찮은데 다음 장이 이상하다" 를
 * 확인하려면 한 장만 봐서는 안 된다.
 *
 * **그림은 들어온 순간 한 번 찍은 것이다.** 화면이 다음 장으로 넘어가도 따라가지
 * 않는다. 상세가 답할 것은 "들어왔을 때 무엇이 떠 있었나" 이고, 지금 것이 다시
 * 필요하면 "다시 찍기" 를 누른다. 찍은 시각은 늘 그림 옆에 밝힌다.
 *
 * 그 한 장이 오기까지 20~40초가 걸린다. 그동안 빈 상자를 두지 않고 서버가 준 마지막
 * 보고(`last_screen`)를 **이전 모습**이라 밝혀 먼저 건다. 그것도 없으면 편성으로 그린다.
 *
 * 운영자가 다른 장으로 넘겨 본 뒤에는 "캡처한 장으로" 로 돌아온다.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Camera, ChevronLeft, ChevronRight, Loader2, Maximize2, PauseCircle, Radio, X } from 'lucide-react';
import type { DisplayPreview, ResolvedItem, ScreenReport } from '../api/client.js';
import { earlierScreen } from './screenCapture.js';
import { ScreenFrame } from './ScreenFrame.js';
import { Badge, relativeTime } from './ui.js';

const ORIGIN_LABEL: Record<string, string> = {
  base: '편성',
  campaign: '캠페인',
  emergency: '긴급',
  direct: '바로 보내기',
};

export function ScreenViewer({
  preview,
  displayName,
  online,
  capture = null,
  capturing = false,
  onRecapture,
  onCaptureMissing,
  onExplain,
  live = true,
  className,
  children,
}: {
  preview: DisplayPreview;
  displayName: string;
  online: boolean;
  /** 카드가 놓이는 격자 안의 자리(칸·줄). 화면 상세가 옆 카드와 높이를 맞출 때 쓴다. */
  className?: string;
  /**
   * 카드 아래에 이어 붙일 내용. 지금은 "오늘의 하루" 가 온다.
   *
   * **같은 질문의 다른 배율이라 한 카드에 둔다** — 지금 이 순간(그림) → 지금 한
   * 바퀴(항목) → 오늘(시간표). 카드를 셋으로 나눠 두면 셋이 서로 다른 것을 묻는
   * 것처럼 보이고, 실제로 그렇게 읽혔다.
   */
  children?: ReactNode;
  /**
   * 들어온 순간 집어 둔 화면의 보고. 이 뒤로 오는 보고로는 바꾸지 않는다.
   *
   * 없으면 아직 오지 않았거나(`capturing`) 끝내 오지 않은 것이다. 그때는 편성으로
   * 그린다 — 찍지 못한 그림을 있는 척하지 않는다.
   */
  capture?: ScreenReport | null;
  /** 화면이 찍어 보내기를 기다리는 중인가. */
  capturing?: boolean;
  /** 지금 화면을 다시 찍어 온다. */
  onRecapture?: () => void;
  /** 찍어 둔 그림이 서버에서 이미 지워졌다 — 화면이 그새 새 그림을 올렸다. */
  onCaptureMissing?: (report: ScreenReport) => void;
  /**
   * "왜 이게 나오나" 를 여는 손잡이. 주면 제목 옆에 물음표가 붙는다.
   *
   * **묻는 자리에 답으로 가는 문을 둔다.** 이 질문은 언제나 눈앞의 그림을 보다가
   * 생긴다 — "이게 왜 나오지". 답을 다른 카드나 설정 창에 두면, 그 순간에 그것을
   * 떠올린 사람만 찾아간다.
   */
  onExplain?: () => void;
  /**
   * 지금을 보고 있는가.
   *
   * 다른 시각을 골라 놓고 "화면이 이걸 보고했다"고 말하면 거짓이다 — 그 보고는
   * 지금 것이고, 그림은 그 시각의 편성이다. 그래서 지금이 아닐 때는 보고를 아예
   * 쓰지 않는다.
   */
  live?: boolean;
}): ReactNode {
  const items = preview.items;

  /*
   * 화면이 보고한 항목에서 시작한다. 보고가 없으면 첫 항목이다 — 루프의 어디쯤인지
   * 서버도 콘솔도 알 수 없으므로 그 이상을 아는 척하지 않는다.
   *
   * **찍어 둔 그림이 있으면 그 그림의 항목이 기준이다.** 화면은 그 뒤로 다음 장으로
   * 넘어가도 여기 걸린 그림은 찍은 순간의 것이다. 하트비트가 알린 지금 항목을
   * 기준으로 삼으면 그림과 제목이 서로 다른 장을 가리키게 된다.
   */
  /** 이전 모습의 그림이 서버에서 이미 지워졌으면 그 보고의 시각. 그 그림은 다시 걸지 않는다. */
  const [earlierGone, setEarlierGone] = useState<string | null>(null);
  /*
   * **이전 모습** — 들어온 순간의 캡처가 오기 전까지 대신 거는 마지막 보고.
   *
   * 그 보고의 항목이 지금 편성에 있을 때만 건다(자산을 모르는 옛 보고는 예외다). 편성에서
   * 빠진 항목의 그림을 걸면 아래 제목과 그림이 서로 다른 장을 가리킨다. 걸면 그 항목이
   * 기준이 된다 — 캡처와 같은 이유다.
   */
  const last = live && !capture ? (preview.last_screen ?? null) : null;
  const lastIndex = reportedIndexOf(items, last?.asset_id ?? null);
  const earlier =
    last && (last.asset_id === null || lastIndex >= 0)
      ? earlierScreen(last, items[lastIndex]?.preview_url ?? null, earlierGone)
      : null;
  const reportedAssetId =
    capture?.asset_id ?? (earlier && lastIndex >= 0 ? earlier.asset_id : null) ?? preview.reported_asset_id;
  const reportedIndex = live ? reportedIndexOf(items, reportedAssetId) : -1;
  /*
   * 직접 넘긴 자리. null 이면 화면이 보고한 자리를 본다 — 이 null 하나가
   * "찍어 둔 장을 봄"과 "내가 골라 보는 중"을 가른다.
   */
  const [manual, setManual] = useState<number | null>(null);
  const [zoomed, setZoomed] = useState(false);

  // 편성이 바뀌거나 새로 찍으면 그 장으로 돌아간다. 그때 내가 고른 자리는 의미가 없다.
  useEffect(() => {
    setManual(null);
  }, [preview.revision, live, capture?.taken_at]);

  const index = viewIndex(manual, reportedIndex, items.length);
  const item: ResolvedItem | null = items[index] ?? null;
  const following = manual === null;
  const showingReported = reportedIndex >= 0 && index === reportedIndex;

  /*
   * 찍어 둔 그림을 쓸 수 있는 조건.
   *
   * **그 그림을 찍은 장을 보고 있을 때만**이다. 운영자가 다음 장으로 넘긴
   * 뒤에도 같은 그림을 붙이면, 보고 있는 항목과 그림이 어긋난 채 "실제 화면"이라는
   * 이름을 달게 된다 — 다음 장이 이상한지 확인하러 넘긴 사람에게 그것은 거짓말이다.
   */
  const shot = live && showingReported ? (capture ?? earlier) : null;
  /** 건 그림이 서버에서 지워졌다. 캡처면 부르는 쪽이 새로 찍고, 이전 모습이면 편성으로 물러난다. */
  const onShotMissing = capture
    ? onCaptureMissing
    : (report: ScreenReport): void => setEarlierGone(report.taken_at);

  const step = (delta: number): void => {
    if (items.length === 0) return;
    setManual(viewIndex(index + delta, reportedIndex, items.length));
  };

  return (
    <>
      <div className={`overflow-hidden rounded-[10px] border border-slate-200 bg-white ${className ?? ''}`}>
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-3 py-3 sm:px-4">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-[15px] font-semibold text-slate-800">
              {live ? '현재 화면' : '선택 시각의 화면'}
            </h2>
            {onExplain ? (
              <button
                type="button"
                className="why-btn"
                title="편성 결정 근거 보기"
                aria-label="편성 결정 근거"
                onClick={onExplain}
              >
                ?
              </button>
            ) : null}
            <SourceBadge
              online={online}
              atNow={live}
              capturing={capturing}
              captured={shot !== null && shot === capture}
              earlier={shot !== null && shot === earlier}
              following={following}
              hasReport={reportedIndex >= 0}
              staleReport={live && Boolean(reportedAssetId) && reportedIndex < 0}
            />
            {/*
              그림이 언제 것인지 밝힌다. 멈춰 있는 그림이라 "몇 초 전" 보다 찍은 시각이
              맞다 — 몇 초 전인지는 짚으면 나온다. 짚을 수 없는 터치 화면에서만 옆에 붙인다.
            */}
            {live && capture ? (
              <span className="text-[11.5px] text-slate-400" title={relativeTime(capture.taken_at)}>
                {clockOf(capture.taken_at, preview.timezone)} 캡처
                <span className="hidden [@media(hover:none)]:inline"> · {relativeTime(capture.taken_at)}</span>
              </span>
            ) : shot !== null && shot === earlier ? (
              /* 이전 모습은 몇 시간 전 것일 수 있다 — 시각만으로는 오늘인지 알 수 없어 얼마 전인지도 적는다. */
              <span className="text-[11.5px] text-slate-400">
                {clockOf(shot.taken_at, preview.timezone)} 캡처 · {relativeTime(shot.taken_at)}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {/* 넘겨 보다가 찍어 둔 장으로 돌아오는 유일한 길. */}
            {!following && reportedIndex >= 0 ? (
              <button type="button" className="btn" onClick={() => setManual(null)}>
                <Radio className="h-3.5 w-3.5" strokeWidth={2} />
                {capture ? '캡처한 장으로' : '보고한 장으로'}
              </button>
            ) : null}
            {live && onRecapture ? (
              <button type="button" className="btn" disabled={capturing} onClick={onRecapture}>
                <Camera className="h-3.5 w-3.5" strokeWidth={2} />
                {capturing ? '캡처 중…' : '다시 캡처'}
              </button>
            ) : null}
            <button
              type="button"
              className="btn"
              disabled={!item}
              onClick={() => setZoomed(true)}
            >
              <Maximize2 className="h-3.5 w-3.5" strokeWidth={2} />
              크게 보기
            </button>
          </div>
        </header>

        <div className={`relative aspect-video w-full overflow-hidden bg-slate-900 ${online ? '' : 'opacity-60'}`}>
          {/*
            캡처를 기다리는 동안에도 빈 상자를 두지 않는다. 이전 모습이나 편성 그림을 먼저 걸고,
            무엇을 걸었는지는 제목 옆 표시와 시각이 밝힌다. 기다리는 중이라는 것은 그림 위에
            따로 붙인다 — 멈춘 것이 아니라 받는 중이라고 알려야 느리다고 읽히지 않는다.
          */}
          <ScreenFrame
            item={item}
            location={displayName}
            timezone={preview.timezone}
            interactive
            shot={shot}
            onShotMissing={onShotMissing}
          />
          {live && capturing && following ? (
            <span className="pointer-events-none absolute right-2 top-2 flex items-center gap-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10.5px] font-medium text-white">
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={2} />
              최신 화면 수신 중
            </span>
          ) : null}
          {/* 멈춘 그림만으로는 화면에서도 영상이 멈춰 있었는지 알 수 없다. */}
          {shot?.kind === 'video' && shot.paused ? (
            <span className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-1 rounded bg-slate-900/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
              <PauseCircle className="h-3 w-3" strokeWidth={2} />
              화면에서 멈춰 있음
            </span>
          ) : null}
          {!online ? (
            <span className="pointer-events-none absolute inset-x-0 top-3 text-center text-[12px] font-medium text-white/85">
              오프라인 · 마지막으로 확인된 화면
            </span>
          ) : null}
        </div>

        <footer className="flex items-center justify-between gap-3 px-3 py-2.5 sm:px-4">
          <div className="min-w-0">
            <p className="truncate text-[13.5px] font-medium text-slate-800">
              {item?.title ?? (preview.source === 'closed' ? '운영 시간 외 — 쉬는 중' : '재생할 항목이 없습니다')}
            </p>
            <p className="text-[11.5px] text-slate-400">
              {item ? `${ORIGIN_LABEL[item.origin] ?? item.origin} · ${shownSeconds(item)}초` : preview.source}
            </p>
          </div>

          {items.length > 1 ? (
            <div className="flex shrink-0 items-center gap-1">
              <button type="button" className="btn btn-tiny" aria-label="이전 항목" onClick={() => step(-1)}>
                <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
              <span className="min-w-[52px] text-center font-mono text-[12px] text-slate-500">
                {index + 1} / {items.length}
              </span>
              <button type="button" className="btn btn-tiny" aria-label="다음 항목" onClick={() => step(1)}>
                <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
              </button>
            </div>
          ) : null}
        </footer>

        {/*
          **시각 손잡이는 그림 바로 아래, 높이가 변하지 않는 자리에 둔다.**

          예전에는 한 바퀴 목록이 이 위에 있었다. 목록 길이는 그 시각의 편성에 따라
          달라지므로(1개짜리 편성에서 5개짜리로 넘어가면 네 줄이 늘어난다), **끌고 있는
          막대가 손 밑에서 위아래로 움직였다.** 조종하는 대상이 조종하는 동안 움직이는
          것은 어떤 이유로도 정당화되지 않는다.

          위에 있는 것은 그림(고정 비율 상자)과 한 줄짜리 footer 뿐이라 높이가 변하지
          않는다. 길이가 변하는 목록은 맨 아래로 보낸다 — 늘어나고 줄어들어도 위를
          밀지 않는다.

          읽는 순서도 이쪽이 낫다: 그림 → 이 시각 → **그 시각에** 도는 것들.
          원인이 결과보다 앞에 온다.
        */}
        {children ? <div className="viewer-extra">{children}</div> : null}

        {/*
          **한 바퀴 전체.**

          위 footer 의 `1 / N` 은 한 번에 한 장씩만 보여준다. blend 캠페인이 베이스
          사이에 광고를 어떤 간격으로 끼웠는지 같은 것은 목록으로 봐야 보인다.
          예전에는 이 목록이 다른 카드에 따로 있어 **넘겨 보는 그림과 이어지지 않았다** —
          여기 두면 눌러서 그 장으로 바로 갈 수 있다.
        */}
        {items.length > 1 ? (
          <div className="rotation">
            <p className="rotation-head">재생 순서 · {items.length}개</p>
            <ol className="rotation-list">
              {items.map((rotationItem, i) => (
                <li key={`${rotationItem.assetId}-${i}`}>
                  <button
                    type="button"
                    className={`rotation-row ${i === index ? 'is-on' : ''}`}
                    aria-current={i === index}
                    onClick={() => setManual(viewIndex(i, reportedIndex, items.length))}
                  >
                    <span className="rotation-no mono">{i + 1}</span>
                    <span className="rotation-title">{rotationItem.title ?? rotationItem.assetId.slice(-6)}</span>
                    <span className="rotation-meta">
                      {ORIGIN_LABEL[rotationItem.origin] ?? rotationItem.origin} · {shownSeconds(rotationItem)}초
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </div>
        ) : null}
      </div>

      {zoomed && item ? (
        <ZoomOverlay
          item={item}
          displayName={displayName}
          timezone={preview.timezone}
          index={index}
          total={items.length}
          onStep={step}
          onClose={() => setZoomed(false)}
          shot={shot}
          onShotMissing={onShotMissing}
        />
      ) : null}
    </>
  );
}

/**
 * 화면이 보고한 항목의 자리.
 *
 * 보고가 없거나(-1) 보고한 것이 지금 편성에 없으면 -1 이다. 두 경우를 같은
 * 값으로 두는 이유는 **둘 다 "지금 이게 나간다" 고 말할 수 없기** 때문이다.
 * 다만 화면에는 다르게 표시한다 — 하나는 "편성 기준", 하나는 "편성과 다름".
 */
export function reportedIndexOf(
  items: readonly { assetId: string }[],
  reportedAssetId: string | null,
): number {
  if (!reportedAssetId) return -1;
  return items.findIndex((item) => item.assetId === reportedAssetId);
}

/**
 * 지금 보여주는 자리.
 *
 * 직접 넘긴 자리가 있으면 그것을, 없으면 화면이 보고한 자리를 본다. 보고가
 * 없으면 첫 항목이다 — 루프의 어디쯤인지는 서버도 콘솔도 모른다.
 */
export function viewIndex(manual: number | null, reportedIndex: number, count: number): number {
  if (count <= 0) return 0;
  const base = manual ?? (reportedIndex >= 0 ? reportedIndex : 0);
  return ((base % count) + count) % count;
}

/**
 * 찍은 시각 — 시:분:초.
 *
 * **화면의 타임존으로 적는다.** 바로 아래 하루 막대와 시각 입력이 화면의 타임존
 * 기준이라, 여기만 보는 사람의 시계로 적으면 같은 카드 안에서 두 시각이 어긋난다.
 */
export function clockOf(iso: string, timezone: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return '';
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  };
  try {
    return at.toLocaleTimeString('ko-KR', { ...options, timeZone: timezone });
  } catch {
    // 모르는 타임존이면 시각을 버리기보다 보는 사람의 시계로라도 적는다.
    return at.toLocaleTimeString('ko-KR', options);
  }
}

/** 지금 보여주는 것이 무엇을 근거로 한 그림인지 밝힌다. */
function SourceBadge({
  online,
  atNow,
  capturing,
  captured,
  earlier,
  following,
  hasReport,
  staleReport,
}: {
  online: boolean;
  /** 지금을 보고 있는가. 아니면 고른 시각의 편성을 계산해 본 것이다. */
  atNow: boolean;
  capturing: boolean;
  /** 화면이 찍어 보낸 그림을 걸고 있는가. */
  captured: boolean;
  /** 캡처 대신 전에 찍힌 그림(이전 모습)을 걸고 있는가. */
  earlier: boolean;
  following: boolean;
  hasReport: boolean;
  staleReport: boolean;
}): ReactNode {
  if (!atNow) return <Badge tone="info">선택 시각</Badge>;
  if (staleReport) return <Badge tone="warn">편성과 다름</Badge>;
  if (capturing) return <Badge tone="info">캡처 중</Badge>;
  if (!hasReport) return <Badge tone="neutral">편성 기준</Badge>;
  if (!following) return <Badge tone="neutral">다른 항목</Badge>;
  // 찍어 둔 그림은 그 뒤에 화면이 꺼져도 그때 찍힌 그림이다. 오프라인은 그림 위 띠가 따로 말한다.
  if (earlier) return <Badge tone="neutral">이전 모습</Badge>;
  if (captured) return <Badge tone="ok">캡처</Badge>;
  if (!online) return <Badge tone="neutral">마지막 보고</Badge>;
  return <Badge tone="neutral">보고한 항목</Badge>;
}

/**
 * 크게 보기.
 *
 * 사이니지는 멀리서 읽히는지가 전부라, 작은 카드로는 글자 크기를 판단할 수 없다.
 * 화면 폭을 최대한 쓰되 16:9 를 지킨다 — 비율이 틀어지면 확인의 의미가 없다.
 */
function ZoomOverlay({
  item,
  displayName,
  timezone,
  index,
  total,
  onStep,
  onClose,
  shot = null,
  onShotMissing,
}: {
  item: ResolvedItem;
  displayName: string;
  timezone: string;
  index: number;
  total: number;
  onStep: (delta: number) => void;
  onClose: () => void;
  /** 화면이 올려 보낸 실제 그림. 크게 볼 때도 같은 것을 보여줘야 한다. */
  shot?: ScreenReport | null;
  onShotMissing?: (report: ScreenReport) => void;
}): ReactNode {
  // 키보드로 넘긴다 — 여러 장을 훑을 때 마우스로 버튼을 겨누는 것은 느리다.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onStep(-1);
      if (event.key === 'ArrowRight') onStep(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);

  // 사이드바 위로 확실히 올라가도록 body 로 포털한다(ui.tsx 의 Modal 과 같은 이유).
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-950/95 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`${displayName} 화면 크게 보기`}
      onClick={onClose}
    >
      <div className="flex items-center justify-between gap-3 pb-3 text-white">
        <div className="min-w-0">
          <p className="truncate text-[14px] font-medium">{displayName}</p>
          <p className="truncate text-[12px] text-white/60">
            {item.title ?? ''} · {index + 1} / {total}
          </p>
        </div>
        <button
          type="button"
          aria-label="닫기"
          className="rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          onClick={onClose}
        >
          <X className="h-5 w-5" strokeWidth={2} />
        </button>
      </div>

      <div
        className="flex min-h-0 flex-1 items-center justify-center"
        role="presentation"
        onClick={(event) => event.stopPropagation()}
      >
        {/* 휴대폰은 주소창이 높이를 먹는다 — 아는 브라우저는 보이는 높이(dvh)로 재야 16:9 가 눌리지 않는다. */}
        <div className="aspect-video max-h-full w-full max-w-[min(100%,calc((100vh-140px)*16/9))] overflow-hidden rounded-lg bg-black supports-[height:100dvh]:max-w-[min(100%,calc((100dvh-140px)*16/9))]">
          <ScreenFrame
            item={item}
            location={displayName}
            timezone={timezone}
            interactive
            shot={shot}
            onShotMissing={onShotMissing}
          />
        </div>
      </div>

      {total > 1 ? (
        <div
          className="flex items-center justify-center gap-3 pt-3"
          role="presentation"
          onClick={(event) => event.stopPropagation()}
        >
          <button type="button" className="btn btn-tiny" aria-label="이전 항목" onClick={() => onStep(-1)}>
            <ChevronLeft className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
          {/* 키보드가 없는 터치 화면에서는 이 안내가 뜻이 없다 — 양쪽 단추가 같은 일을 한다. */}
          <span className="font-mono text-[12px] text-white/70 [@media(hover:none)]:hidden">← → 키로 이동</span>
          <button type="button" className="btn btn-tiny" aria-label="다음 항목" onClick={() => onStep(1)}>
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

/**
 * 항목이 화면에 머무는 시간(초). **영상은 끝까지 재생하므로 영상 길이다** — 해석 결과의 `dwellSec` 는 옛
 * 화면용으로 길이를 올림한 값이라(core `videoDwellSec`) 그대로 적으면 30초 영상이 "31초" 로 보인다.
 */
export function shownSeconds(item: Pick<ResolvedItem, 'type' | 'durationSec' | 'dwellSec'>): number {
  if (item.type === 'video' && item.durationSec) return Math.round(item.durationSec);
  return item.dwellSec;
}
